import React, { useEffect, useMemo, useState } from 'react';
import {
    AppBar,
    Toolbar,
    Box,
    Link as MuiLink,
    IconButton,
    Button,
    Typography,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    SwipeableDrawer,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

// Social icons
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';

// Menu-item icons
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import MuseumIcon from '@mui/icons-material/Museum';
import TrainIcon from '@mui/icons-material/Train';
import EventIcon from '@mui/icons-material/Event';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import ArchiveIcon from '@mui/icons-material/Archive';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import PlaceIcon from '@mui/icons-material/Place';
import EmailIcon from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import NewspaperIcon from '@mui/icons-material/Newspaper'; // NEW

import logo from '../assets/header/DepotLogo2.png';

// Navy palette
const NAVY = '#002F6C';
const NAVY_DARK = '#001F4C';
const GOLD = '#FFC94D';

const topLinks = [
    { label: 'About', to: '/about', Icon: InfoIcon },
    { label: 'Contact', to: '/contact', Icon: EmailIcon },
    { label: 'Location', to: '/location', Icon: PlaceIcon },
];

const menuItems = [
    { label: 'Home', to: '/', Icon: HomeIcon },
    { label: 'Exhibits', to: '/exhibits', Icon: MuseumIcon },
    { label: 'News', to: '/news', Icon: NewspaperIcon }, // NEW
    { label: 'Visit the Museum', to: '/visit', Icon: TrainIcon },
    { label: 'Membership', to: '/membership', Icon: GroupAddIcon },
    { label: 'Volunteers', to: '/volunteer', Icon: Diversity3Icon },
    { label: 'Donate', to: '/donate', Icon: VolunteerActivismIcon },
    { label: 'Archives', to: '/archives', Icon: ArchiveIcon },
    { label: 'Links', to: '/links', Icon: LinkIcon },
];

export default function Header() {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const navigate = useNavigate();
    const location = useLocation();

    const [open, setOpen] = useState(false);
    const [hide, setHide] = useState(false);
    const [lastY, setLastY] = useState(0);

    // Hide-on-scroll only for small screens
    useEffect(() => {
        if (isMdUp) return;
        const onScroll = () => {
            const y = window.scrollY || 0;
            const delta = y - lastY;
            if (y > 64 && delta > 6) setHide(true);
            if (delta < -6) setHide(false);
            setLastY(y);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [isMdUp, lastY]);

    useEffect(() => setOpen(false), [location.pathname]);

    const handleNav = (to) => () => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        navigate(to);
    };

    const SocialRow = useMemo(
        () => (
            <Box sx={{ display: 'flex', gap: 0.5, color: '#fff' }}>
                <IconButton
                    component="a"
                    href="https://www.facebook.com/piedmontalabamamuseum"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'inherit' }}
                    aria-label="Facebook"
                >
                    <FacebookIcon />
                </IconButton>
                <IconButton
                    component="a"
                    href="https://www.instagram.com/piedmontalabamamuseum/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'inherit' }}
                    aria-label="Instagram"
                >
                    <InstagramIcon />
                </IconButton>
            </Box>
        ),
        []
    );

    return (
        <>
            <AppBar
                /* Desktop: scrolls with the page; Mobile: sticky */
                position={isMdUp ? 'static' : 'sticky'}
                elevation={0}
                sx={{
                    bgcolor: NAVY,
                    color: '#fff',
                    boxShadow: 'none',
                    transform: { xs: hide ? 'translateY(-100%)' : 'translateY(0)', md: 'none' },
                    transition: 'transform .25s ease',
                    borderBottom: `1px solid ${NAVY_DARK}`,
                    zIndex: (t) => t.zIndex.appBar + 1,
                }}
            >
                {/* Desktop header */}
                <Toolbar
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        justifyContent: 'center',
                        px: 4,
                        py: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {/* Logo */}
                        <Box component={RouterLink} to="/" sx={{ display: 'inline-flex' }}>
                            <Box
                                component="img"
                                src={logo}
                                alt="Piedmont Historical Society"
                                loading="lazy"
                                decoding="async"
                                sx={{ height: 185, width: 'auto' }}
                            />
                        </Box>

                        {/* Right of logo: socials, top links, partnership line */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.75,
                            }}
                        >
                            {SocialRow}
                            <Box sx={{ display: 'flex', gap: 3 }}>
                                {topLinks.map(({ label, to }) => {
                                    const active = location.pathname === to;
                                    return (
                                        <MuiLink
                                            key={label}
                                            component={RouterLink}
                                            to={to}
                                            underline="none"
                                            sx={{
                                                color: active ? GOLD : '#fff',
                                                fontWeight: active ? 700 : 500,
                                                '&:hover': {
                                                    color: GOLD,
                                                    textDecoration: 'underline',
                                                },
                                            }}
                                        >
                                            {label}
                                        </MuiLink>
                                    );
                                })}
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ color: 'rgba(255,255,255,0.85)' }}
                                >
                                    In partnership with{' '}
                                    <MuiLink
                                        href="https://tourism.alabama.gov/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            color: GOLD,
                                            textDecoration: 'underline',
                                            '&:hover': {
                                                color: '#fff',        // or another color you want
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        Alabama Tourism Department
                                    </MuiLink>
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Toolbar>

                {/* Desktop secondary menu (icons) */}
                <Box
                    component="nav"
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        justifyContent: 'center',
                        bgcolor: NAVY_DARK,
                        py: 1,
                    }}
                >
                    {menuItems.map(({ label, to, Icon }) => {
                        const active = location.pathname === to;
                        return (
                            <Button
                                key={label}
                                component={RouterLink}
                                to={to}
                                startIcon={<Icon htmlColor={active ? GOLD : '#fff'} />}
                                sx={{
                                    color: active ? GOLD : '#fff',
                                    fontSize: '1.1rem',
                                    textTransform: 'none',
                                    mx: 2,
                                    fontWeight: active ? 700 : 500,
                                    '&:hover': {
                                        color: GOLD,
                                        bgcolor: 'rgba(255,255,255,0.06)',
                                    },
                                }}
                            >
                                {label}
                            </Button>
                        );
                    })}
                </Box>

                {/* Mobile header: logo + social icons + hamburger */}
                <Toolbar
                    sx={{
                        display: { xs: 'flex', md: 'none' },
                        px: 2,
                        bgcolor: NAVY,
                        color: '#fff',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                flexGrow: 1,
                            }}
                        >
                            <Box
                                component={RouterLink}
                                to="/"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                }}
                                aria-label="Go to homepage"
                            >
                                <Box
                                    component="img"
                                    src={logo}
                                    alt="Piedmont Historical Society"
                                    loading="lazy"
                                    decoding="async"
                                    sx={{ height: 84, width: 'auto' }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: { xs: 'inline-flex', md: 'none' },
                                    ml: 0.5,
                                }}
                            >
                                {SocialRow}
                            </Box>
                        </Box>

                        <IconButton
                            aria-label="Open menu"
                            onClick={() => setOpen(true)}
                            sx={{ color: '#fff' }}
                        >
                            <MenuIcon />
                        </IconButton>
                    </Box>
                </Toolbar>

                {/* Partnership line – mobile */}
                <Box
                    sx={{
                        display: { xs: 'flex', md: 'none' },
                        justifyContent: 'center',
                        alignItems: 'center',
                        bgcolor: NAVY_DARK,
                        borderTop: `1px solid ${NAVY_DARK}`,
                        py: 0.5,
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255,255,255,0.85)' }}
                    >
                        In partnership with{' '}
                        <MuiLink
                            href="https://tourism.alabama.gov/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: GOLD,
                                textDecoration: 'underline',
                                '&:hover': {
                                    color: '#fff',        // or another color you want
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Alabama Tourism Department
                        </MuiLink>
                    </Typography>
                </Box>
            </AppBar>

            {/* Mobile drawer */}
            <SwipeableDrawer
                PaperProps={{ sx: { width: '85vw', maxWidth: 380 } }}
                anchor="right"
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                disableBackdropTransition={false}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, flex: 1 }}
                    >
                        Menu
                    </Typography>
                    <IconButton
                        aria-label="Close menu"
                        onClick={() => setOpen(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />

                <List sx={{ py: 0 }}>
                    {menuItems.map(({ label, to, Icon }) => {
                        const active = location.pathname === to;
                        return (
                            <ListItemButton
                                key={label}
                                selected={active}
                                onClick={handleNav(to)}
                            >
                                <ListItemIcon>
                                    <Icon color={active ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText primary={label} />
                            </ListItemButton>
                        );
                    })}
                </List>

                <Divider />

                {/* More links */}
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography
                        variant="overline"
                        sx={{ color: 'text.secondary' }}
                    >
                        More
                    </Typography>
                </Box>
                <List sx={{ py: 0 }}>
                    {topLinks.map(({ label, to, Icon }) => {
                        const active = location.pathname === to;
                        return (
                            <ListItemButton
                                key={label}
                                selected={active}
                                onClick={handleNav(to)}
                            >
                                <ListItemIcon>
                                    <Icon color={active ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText primary={label} />
                            </ListItemButton>
                        );
                    })}
                </List>

                {/* Social row */}
                <Divider />
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Follow us
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                            component="a"
                            href="https://www.facebook.com/profile.php?id=6157970910905"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Facebook"
                        >
                            <FacebookIcon />
                        </IconButton>
                        <IconButton
                            component="a"
                            href="https://www.instagram.com/piedmontalabamamuseum/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram"
                        >
                            <InstagramIcon />
                        </IconButton>
                    </Box>
                </Box>
            </SwipeableDrawer>
        </>
    );
}
